import React, { forwardRef } from "react";

import { cn } from "@/lib/utils";

export type TextInputProps = {
  readonly id?: string;
  readonly name: string;
  readonly value: string;
  readonly placeholder?: string;
  readonly type?: string;
  readonly className?: string;
  readonly disabled?: boolean;
  readonly onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readonly autoComplete?: "on" | "off";
  readonly onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  readonly onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  readonly "aria-invalid"?: boolean;
  readonly "aria-describedby": string;
  readonly leftIcon?: React.ReactNode;
  readonly rightIcon?: React.ReactNode;
  readonly readOnly?: boolean;
  readonly min?: string | number;
  readonly step?: string | number;
  readonly onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  readonly maxLength?: number;
  readonly inputMode?:
    | "none"
    | "text"
    | "decimal"
    | "numeric"
    | "email"
    | "url"
    | "search"
    | "tel";
};

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (props, ref): React.ReactElement => {
    const {
      id,
      name,
      placeholder,
      type = "text",
      className = "",
      disabled,
      autoComplete = "on",
      onBlur,
      onFocus,
      onChange,
      value,
      leftIcon,
      rightIcon,
      readOnly,
      min,
      step,
      inputMode = "text",
      onClick,
      maxLength,
    } = props;

    return (
      <div className="TextInput relative">
        {leftIcon && (
          <div className="absolute left-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center">
            {leftIcon}
          </div>
        )}
        <input
          aria-describedby={props["aria-describedby"]}
          aria-invalid={props["aria-invalid"]}
          autoComplete={autoComplete}
          className={cn(
            "h-10",
            "-ring-offset-1 peer block w-full appearance-none rounded border-none px-3 ring-1 ring-light-grey focus:outline-none focus:ring-1 focus:!ring-blue-400 aria-invalid:ring-1 aria-invalid:ring-red aria-invalid:placeholder:text-red",
            "placeholder:!text-secondary/40",
            "[color-scheme:light]",
            className,
            leftIcon && "pl-9",
            rightIcon && "pr-9",
            disabled && "pointer-events-none opacity-50"
          )}
          disabled={disabled}
          data-field={name}
          id={id}
          inputMode={inputMode}
          min={min}
          name={name}
          placeholder={placeholder}
          readOnly={disabled || readOnly}
          ref={ref}
          step={step}
          type={type}
          value={value}
          onBlur={onBlur}
          onChange={onChange}
          onClick={onClick}
          onFocus={onFocus}
          maxLength={maxLength}
        />
        {rightIcon && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {rightIcon}
          </div>
        )}
      </div>
    );
  }
);

TextInput.displayName = "TextInput";

export default TextInput;
